import React, { useLayoutEffect, useRef, useEffect, useContext } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AOS from "aos";
import "aos/dist/aos.css"; // Import the CSS for AOS animations
import useBreakpoint from "../helpers/useBreakpoint";
import { Helmet } from "react-helmet";
import classNames from "classnames";

import Title from "../components/global/Title";
import Pill from "../components/specific/home/sobre/Pill";
import Contador from "../components/specific/home/sobre/Contador";
import ContadorSmall from "../components/specific/home/sobre/ContadorSmall";
import UndelinedQuote from "../components/global/UndelinedQuote";
import Paragraph from "../components/global/Paragraph";
import { LanguageContext } from "../App";

const slugify = require("slugify");

gsap.registerPlugin(ScrollTrigger);

export default function Sobre() {
  const { currentLanguage } = useContext(LanguageContext);

  const currentBreakpoint = useBreakpoint();
  const main = useRef();

  const titleRef = useRef(null);
  const imageRef = useRef(null);
  const image2Ref = useRef(null);
  const imageContainerRef = useRef(null);
  const image2ContainerRef = useRef(null);
  const subtitleRef = useRef(null);
  const introSubtitleRef = useRef(null);

  useEffect(() => {
    AOS.init({
      disable: "phone",
      anchorPlacement: "top-top",
      delay:
        currentBreakpoint === "xs" ||
        currentBreakpoint === "sm" ||
        currentBreakpoint === "md"
          ? 100
          : 1000,
    });
  }, []); // Run this effect only once after initial render

  useLayoutEffect(() => {
    // Set initial opacity to 0 before the animation starts
    if (
      currentBreakpoint === "sm" ||
      currentBreakpoint === "md" ||
      currentBreakpoint === "lg" ||
      currentBreakpoint === "xl" ||
      currentBreakpoint === "2xl"
    ) {
      gsap.set(imageContainerRef.current, {
        scaleY: 0,
        transformOrigin: "top",
        zIndex: 10,
        position: "relative",
      });
      gsap.set(imageRef.current, { opacity: 0 });
      gsap.set(image2ContainerRef.current, {
        scaleY: 0,
        transformOrigin: "top",
        zIndex: 10,
        position: "relative",
      });
      gsap.set(image2Ref.current, { opacity: 0 });
      gsap.set(titleRef.current, {
        opacity: 0,
        y: 50,
        zIndex: 20,
        position: "relative",
      });
      gsap.set(subtitleRef.current, { opacity: 0 });
      gsap.set(introSubtitleRef.current, { opacity: 0 });

      // GSAP animation timeline
      const animationTimeline = gsap.timeline();

      animationTimeline.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
        delay: 0.5,
      });

      animationTimeline.addLabel("imageAnimations");

      animationTimeline.to(imageContainerRef.current, {
        scaleY: 1,
        duration: 0.75,
        ease: "power2.out",
      });

      animationTimeline.to(
        image2ContainerRef.current,
        {
          scaleY: 1,
          duration: 0.75,
          ease: "power2.out",
        },
        "imageAnimations+=0.1",
      );

      animationTimeline.addLabel("textAnimations");

      animationTimeline.to(image2Ref.current, {
        opacity: 1,
        duration: 0.5,
        ease: "power2.out",
      });

      animationTimeline.to(
        imageRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: "power2.out",
        },
        "textAnimations",
      );

      animationTimeline.to(
        subtitleRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: "power2.out",
        },
        "textAnimations",
      );

      animationTimeline.to(
        introSubtitleRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: "power2.out",
        },
        "textAnimations",
      );

      return () => {
        animationTimeline.kill();
      };
    }
  }, [currentBreakpoint]);

  const contadoresData = [
    {
      title: currentLanguage === "PT" ? "Anos" : "Years",
      quantity: 25,
    },
    {
      title: currentLanguage === "PT" ? "Colaboradores" : "Employees",
      quantity: 30,
    },
    {
      title: currentLanguage === "PT" ? "M² de Laboratório" : "Sq. Ft. of Lab",
      quantity: 1500,
    },
    {
      title: currentLanguage === "PT" ? "Clientes" : "Clients",
      quantity: 200,
    },
  ];

  const contadores = contadoresData.map((item, index) => {
    return <Contador key={index} index={index} item={item} />;
  });

  const acreditacoesData = [
    {
      title:
        currentLanguage === "PT" ? "Laboratório Central" : "Central Laboratory",
      img: "laboratorio-central",
      location: "L0730-1 - Vila Nova de Gaia",
      pills:
        currentLanguage === "PT"
          ? ["Betões, cimentos e argamassas", "Agregados e Inertes"]
          : [
              "Concrete, cements, and mortars",
              "Aggregates and Inert Materials",
            ],
      file: "laboratorio-central.pdf",
    },
    {
      title: currentLanguage === "PT" ? "Laboratório" : "Laboratory",
      img: "laboratorio",
      location: "L0730-2 - Ribeira de Pena",
      pills:
        currentLanguage === "PT"
          ? [
              "Betões, cimentos e argamassas",
              "Agregados e Inertes",
              "Metais e ligas metálicas",
            ]
          : [
              "Concrete, cements, and mortars",
              "Aggregates and Inert Materials",
              "Metals and Metal Alloys",
            ],
      file: false,
    },
  ];

  const acreditacoes = acreditacoesData.map((item, index) => {
    return (
      <div
        className={classNames(
          "relative mb-12 flex flex-col rounded-sm  bg-white px-8 pb-56 pt-10 transition duration-500 md:w-1/2 lg:w-1/2 lg:px-12 lg:pt-14 lg:hover:-translate-y-8 lg:hover:drop-shadow-xl xl:w-[37%] 2xl:w-[35%] 2xl:px-20 2xl:pb-72",
        )}
        data-aos="fade-up"
        data-aos-delay={index === 0 ? 0 : 1000}
      >
        <div className="mb-8 flex flex-col gap-y-2 lg:mb-12">
          <h3 className="text-sm font-medium uppercase tracking-wider text-green-1">
            Acreditação
          </h3>
          <h2 className="text-2xl font-semibold  uppercase text-green-3">
            {item.title}
          </h2>
          <p className="text-sm text-dark-3">{item.location}</p>
        </div>
        <div className="flex flex-grow flex-col justify-center gap-y-5 2xl:gap-y-9">
          {item.pills.map((pill, indexPill) => (
            <Pill key={indexPill} text={pill} />
          ))}
          <img
            src={`${process.env.PUBLIC_URL}/img/sobre/${item.img}.jpg`}
            srcSet={`${process.env.PUBLIC_URL}/img/sobre/${item.img}-2x.jpg 2x`}
            alt="Tick"
            className="mask-triangle-right-2 absolute bottom-0 right-0 h-auto w-full"
            data-aos="fade-left"
          />
          {item.file && (
            <a
              className="mask-triangle-left absolute bottom-0 left-0 h-40 w-40 bg-green-1 bg-download bg-[length:2rem] bg-[2.5rem_center] bg-no-repeat text-transparent transition-colors hover:bg-green-2 focus:bg-green-2 active:bg-green-2 active:hover:bg-green-3"
              target="_blank"
              rel="noreferrer"
              href={`${process.env.PUBLIC_URL}/files/acreditacao-${item.file}`}
            >
              Download
            </a>
          )}
        </div>
        {!item.file && (
          <span className="absolute -bottom-10 right-0 text-[#B3B4B7]">
            {currentLanguage === "PT"
              ? "Em suspensão voluntária temporária"
              : "In voluntary temporary suspension"}
          </span>
        )}
      </div>
    );
  });

  return (
    <>
      <Helmet>
        <title>
          {currentLanguage === "PT" ? "Sobre Nós" : "About Us"} - Senqual
        </title>
        <meta
          name="description"
          content={
            currentLanguage === "PT"
              ? "Rigor e profissionalismo na base de trabalho das nossas equipas."
              : "Rigor and Professionalism are the pillars of our team."
          }
        />
      </Helmet>
      <div
        className="mt-14 flex flex-col overflow-x-hidden bg-light-2 lg:pb-32"
        ref={main}
      >
        <div className="container mx-auto mb-16 px-8 lg:mb-0">
          <div
            className="mt-4 h-24 w-1/2 md:mb-8 lg:mb-0 lg:mt-12 lg:w-1/5"
            ref={introSubtitleRef}
          >
            <UndelinedQuote
              text={
                <span>
                  {currentLanguage === "PT"
                    ? "Rigor e profissionalismo na base de trabalho das nossas equipas."
                    : "Rigor and Professionalism are the pillars of our team."}
                </span>
              }
            />
          </div>
          <div ref={titleRef}>
            <Title
              text={
                <span>
                  {currentLanguage === "PT" ? "Sobre Nós" : "About Us"}
                </span>
              }
              classes="md:-mt-16 lg:-mt-8 xl:-mt-16"
            />
          </div>
          <div className="-mt-5 flex w-full flex-wrap justify-between lg:mb-24 xl:-mt-6 xl:mb-32 2xl:-mt-8 2xl:flex-nowrap 2xl:gap-x-20">
            <div className="flex w-[calc(40%-0.375rem)] lg:w-1/4 2xl:w-auto">
              <figure
                className="flex h-60 w-full items-center justify-center overflow-hidden bg-green-4 sm:h-80 md:h-96 lg:h-auto lg:self-end"
                ref={imageContainerRef}
              >
                <img
                  src={process.env.PUBLIC_URL + "/img/sobre/main-1.jpg"}
                  srcSet={process.env.PUBLIC_URL + "/img/sobre/main-1-2x.jpg"}
                  alt={
                    currentLanguage === "PT"
                      ? "Sobre Nós - Senqual"
                      : "About Us - Senqual"
                  }
                  className="h-full w-full object-cover"
                  ref={imageRef}
                />
              </figure>
            </div>
            <div className="flex w-[calc(60%-0.375rem)] lg:w-2/5">
              <figure
                className="flex h-60 w-full items-center justify-center overflow-hidden bg-green-4 sm:h-80 md:h-96 lg:h-auto"
                ref={image2ContainerRef}
              >
                <img
                  src={process.env.PUBLIC_URL + "/img/sobre/main-2.jpg"}
                  srcSet={process.env.PUBLIC_URL + "/img/sobre/main-2-2x.jpg"}
                  alt={
                    currentLanguage === "PT"
                      ? "Sobre Nós - Senqual"
                      : "About Us - Senqual"
                  }
                  className="h-full w-full object-cover"
                  ref={image2Ref}
                />
              </figure>
            </div>
            <div
              className="mt-12 w-full md:w-3/4 lg:mt-0 lg:w-1/4 lg:self-center  2xl:w-1/4"
              ref={subtitleRef}
            >
              <Paragraph
                classes="font-medium"
                text={
                  <span>
                    {currentLanguage === "PT"
                      ? "A SENQUAL presta serviços de consultoria e controlo de qualidade no âmbito da Indústria da Construção Civil e Obras Públicas."
                      : "SENQUAL provides consulting and quality control services in the field of Civil Construction Industry and Public Works."}
                  </span>
                }
              />
            </div>
          </div>
        </div>

        <section className="container mx-auto px-8">
          <div className="mb-16 flex flex-wrap justify-between gap-y-16 lg:mb-24 xl:mb-32">
            {contadores}
          </div>
        </section>
        <section className="container mx-auto px-8">
          <div className="ml:gap-x-32 relative mb-16 items-center justify-between after:absolute after:left-1/2 after:w-0.5 after:bg-green-1 lg:mb-24 lg:flex lg:after:h-full lg:after:content-[''] xl:mb-32">
            <p
              className="mb-4 text-dark-2 md:w-3/4 lg:mb-0 lg:w-5/12"
              data-aos="fade-up"
            >
              {currentLanguage === "PT"
                ? "As exigências atuais do mercado conduzem à necessidade de controlo efetivo por empresas especializadas, em todas as fases de construção, existindo um vasto mercado em que os serviços da SENQUAL se tornam indispensáveis."
                : "Current market demands lead to the need for effective control by specialized companies in all construction phases, creating a vast market where SENQUAL's services become indispensable."}
            </p>
            <hr className="w-px bg-green-1" data-aos="fade-up" />
            <p
              className="text-dark-2 md:w-3/4 lg:w-5/12"
              data-aos="fade-up"
              data-aos-delay="500"
            >
              {currentLanguage === "PT"
                ? "No mercado há mais de 25 anos, o posicionamento da SENQUAL caracteriza-se pelo profissionalismo, empenho e conhecimento dos seus colaboradores e pelo rigor na aplicação de toda a legislação e normalização em vigor. A par com a execução dos trabalhos laboratoriais ou in situ, é fornecido todo o apoio técnico necessário à realização dos mesmos, bem como à interpretação dos resultados obtidos."
                : "In the market for over 25 years, SENQUAL is characterized by the professionalism, dedication, and expertise of its employees, and the rigor in complying with all applicable legislation and standards. Alongside the execution of laboratory or on-site work, all necessary technical support for their completion is provided, including interpreting the obtained results."}
            </p>
          </div>
        </section>
        <section
          className="relative mb-16 bg-green-1 after:absolute after:bottom-0 after:left-0 after:h-24 after:w-full after:bg-light-2 after:content-[''] md:after:h-16 lg:after:h-24 xl:mb-28 xl:after:h-28 2xl:after:h-32"
          data-aos={
            currentBreakpoint === "sm" || currentBreakpoint === "md"
              ? "fade-up"
              : false
          }
        >
          <div
            className="mask-triangle-right animation-1 absolute right-0 top-0 z-20 hidden h-[46.5rem] w-1/3 bg-green-4 bg-lab bg-cover bg-center xl:block 2xl:h-[52rem]"
            data-aos="fade-left"
            data-aos-delay="1500"
          ></div>
          <figure className="mb-16 h-0 w-full overflow-hidden bg-green-4 pb-[66.5%] md:hidden">
            <img
              src={process.env.PUBLIC_URL + "/img/sobre/lab-mobile.jpg"}
              srcSet={process.env.PUBLIC_URL + "/img/sobre/lab-mobile-2x.jpg"}
              alt={
                currentLanguage === "PT"
                  ? "Sobre Nós - Senqual"
                  : "About Us - Senqual"
              }
            />
          </figure>
          <div className="container mx-auto h-full px-8 md:flex md:gap-x-24 xl:gap-x-0">
            <p
              className="mb-16 text-white md:order-2 md:mb-0 md:w-1/2 md:self-start md:py-12 lg:py-[6.5rem] xl:w-1/3 xl:py-[6.5rem] xl:pl-12 2xl:py-[8rem]"
              data-aos="fade-up"
              data-aos-delay="1000"
            >
              {currentLanguage === "PT" ? (
                <>
                  A Senqual dispõe de um <b>laboratório</b> central localizado
                  em Vila Nova de Gaia. Dispõe ainda de laboratórios que instala
                  nas obras, de acordo com as necessidades e dimensão das
                  mesmas. Complementarmente a Senqual disponibiliza equipas para
                  ensaios e trabalhos pontuais a realizar in&nbsp;situ.
                </>
              ) : (
                <>
                  Senqual has a central <b>laboratory</b> located in Vila Nova
                  de Gaia. It also has laboratories installed on construction
                  sites, according to their needs and size. Additionally,
                  Senqual provides teams for on-site tests and
                  occasional&nbsp;work.
                </>
              )}
            </p>

            <div
              className="relative z-10 md:order-1 md:w-2/5 xl:w-1/3"
              data-aos="fade-up"
              data-aos-delay="500"
            >
              <ContadorSmall
                item={{
                  title:
                    currentLanguage === "PT"
                      ? "Obras Controladas"
                      : "Controlled Works",
                  quantity: 1200,
                }}
              />
              <div className="relative z-20 mx-auto lg:w-full">
                <svg
                  width="100%"
                  height="auto"
                  viewBox="0 0 368 255"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto"
                >
                  <path
                    d="M332.937 182.606C332.937 143.398 317.362 105.795 289.637 78.0707C261.913 50.346 224.31 34.7705 185.101 34.7705C145.893 34.7705 108.29 50.346 80.5656 78.0707C52.841 105.795 37.2654 143.398 37.2654 182.606"
                    stroke="#CDCED1"
                    strokeWidth="68.7609"
                  />
                  <path
                    d="M37.2654 182.606C37.2654 143.398 52.8328 105.795 80.543 78.0707C108.253 50.346 145.836 34.7705 185.024 34.7705C224.212 34.7705 261.795 50.346 289.505 78.0707"
                    stroke="#004E38"
                    strokeWidth="68.7609"
                    id="animated-path"
                  />

                  <circle
                    cx="184.996"
                    cy="182.502"
                    r="62.22"
                    fill="#CDCED1"
                    stroke="#F3F3F3"
                    strokeWidth="19.2982"
                  />
                  <path
                    d="M184.664 206.458C196.866 206.458 206.956 196.367 206.956 184.165C206.956 171.941 196.866 161.873 184.641 161.873C172.439 161.873 162.371 171.941 162.371 184.165C162.371 196.367 172.462 206.458 184.664 206.458ZM184.664 203.569C173.906 203.569 165.26 194.9 165.26 184.165C165.26 173.408 173.906 164.74 184.641 164.74C195.399 164.74 204.067 173.408 204.089 184.165C204.089 194.9 195.421 203.569 184.664 203.569ZM184.641 172.674C185.753 172.674 186.686 171.763 186.686 170.652C186.686 169.518 185.753 168.607 184.641 168.607C183.53 168.607 182.619 169.518 182.619 170.652C182.619 171.763 183.53 172.674 184.641 172.674ZM181.441 187.144C183.086 188.744 185.286 188.366 186.686 186.455L195.977 174.097C196.688 173.119 195.688 172.163 194.732 172.83L182.085 181.854C180.174 183.21 179.796 185.477 181.441 187.144ZM175.129 176.742C176.24 176.742 177.151 175.808 177.151 174.697C177.151 173.586 176.24 172.674 175.129 172.674C174.017 172.674 173.084 173.586 173.084 174.697C173.084 175.808 174.017 176.742 175.129 176.742ZM171.172 186.21C172.284 186.21 173.195 185.277 173.195 184.165C173.195 183.054 172.284 182.143 171.172 182.143C170.061 182.143 169.128 183.054 169.128 184.165C169.128 185.277 170.061 186.21 171.172 186.21ZM198.133 186.21C199.244 186.21 200.177 185.277 200.177 184.165C200.177 183.054 199.244 182.143 198.133 182.143C197.021 182.143 196.11 183.054 196.11 184.165C196.11 185.277 197.021 186.21 198.133 186.21ZM175.151 195.678C176.284 195.678 177.196 194.767 177.196 193.656C177.196 192.522 176.284 191.611 175.151 191.611C174.04 191.611 173.106 192.522 173.106 193.656C173.106 194.767 174.04 195.678 175.151 195.678ZM194.154 195.678C195.265 195.678 196.177 194.767 196.177 193.656C196.177 192.522 195.265 191.611 194.154 191.611C193.021 191.611 192.109 192.522 192.109 193.656C192.109 194.767 193.021 195.678 194.154 195.678Z"
                    fill="#004E38"
                  />
                  <path
                    d="M22.2657 211.807C22.177 211.807 22.1016 211.781 22.0395 211.727C21.9863 211.665 21.9596 211.59 21.9596 211.501V204.048L19.7503 205.751C19.6793 205.805 19.6039 205.827 19.5241 205.818C19.4442 205.809 19.3777 205.769 19.3244 205.698L18.9784 205.259C18.9252 205.179 18.903 205.099 18.9119 205.019C18.9296 204.94 18.974 204.873 19.0449 204.82L21.9463 202.584C22.0084 202.54 22.0661 202.513 22.1193 202.504C22.1726 202.495 22.2303 202.491 22.2924 202.491H22.9711C23.0599 202.491 23.1308 202.522 23.1841 202.584C23.2373 202.637 23.2639 202.708 23.2639 202.797V211.501C23.2639 211.59 23.2373 211.665 23.1841 211.727C23.1308 211.781 23.0599 211.807 22.9711 211.807H22.2657ZM26.8538 211.807C26.774 211.807 26.7074 211.781 26.6542 211.727C26.6098 211.665 26.5877 211.594 26.5877 211.514C26.5877 211.47 26.5921 211.434 26.601 211.408C26.6187 211.381 26.632 211.359 26.6409 211.341L28.6772 208.506C28.4554 208.56 28.2158 208.586 27.9585 208.586C27.4616 208.577 27.018 208.489 26.6276 208.32C26.2372 208.152 25.9045 207.925 25.6294 207.641C25.3544 207.349 25.1458 207.02 25.0039 206.657C24.8619 206.293 24.7909 205.911 24.7909 205.512C24.7909 205.122 24.8575 204.74 24.9906 204.367C25.1325 203.995 25.341 203.658 25.6161 203.356C25.9 203.054 26.2505 202.815 26.6675 202.637C27.0845 202.451 27.577 202.358 28.1448 202.358C28.7038 202.358 29.1918 202.446 29.6088 202.624C30.0347 202.792 30.3896 203.028 30.6736 203.329C30.9664 203.622 31.1793 203.955 31.3124 204.327C31.4544 204.7 31.5253 205.086 31.5253 205.485C31.5253 205.849 31.4721 206.182 31.3656 206.483C31.2592 206.785 31.1305 207.056 30.9797 207.295C30.8377 207.535 30.6957 207.757 30.5538 207.961L27.9984 211.541C27.9629 211.585 27.9141 211.643 27.852 211.714C27.7899 211.776 27.6968 211.807 27.5725 211.807H26.8538ZM28.1448 207.508C28.5175 207.508 28.8591 207.428 29.1696 207.269C29.489 207.1 29.7419 206.869 29.9282 206.577C30.1146 206.275 30.2077 205.916 30.2077 205.499C30.2077 205.082 30.1146 204.727 29.9282 204.434C29.7419 204.132 29.489 203.902 29.1696 203.742C28.8591 203.573 28.5175 203.489 28.1448 203.489C27.781 203.489 27.4439 203.573 27.1333 203.742C26.8228 203.902 26.5744 204.132 26.388 204.434C26.2017 204.727 26.1085 205.082 26.1085 205.499C26.1085 205.916 26.2017 206.275 26.388 206.577C26.5744 206.869 26.8228 207.1 27.1333 207.269C27.4439 207.428 27.781 207.508 28.1448 207.508ZM34.7431 211.807C34.6633 211.807 34.5967 211.781 34.5435 211.727C34.4991 211.665 34.4769 211.594 34.4769 211.514C34.4769 211.47 34.4814 211.434 34.4903 211.408C34.508 211.381 34.5213 211.359 34.5302 211.341L36.5665 208.506C36.3447 208.56 36.1051 208.586 35.8478 208.586C35.3509 208.577 34.9073 208.489 34.5169 208.32C34.1265 208.152 33.7937 207.925 33.5187 207.641C33.2436 207.349 33.0351 207.02 32.8932 206.657C32.7512 206.293 32.6802 205.911 32.6802 205.512C32.6802 205.122 32.7468 204.74 32.8799 204.367C33.0218 203.995 33.2303 203.658 33.5054 203.356C33.7893 203.054 34.1398 202.815 34.5568 202.637C34.9738 202.451 35.4663 202.358 36.0341 202.358C36.5931 202.358 37.0811 202.446 37.4981 202.624C37.924 202.792 38.2789 203.028 38.5628 203.329C38.8556 203.622 39.0686 203.955 39.2017 204.327C39.3436 204.7 39.4146 205.086 39.4146 205.485C39.4146 205.849 39.3614 206.182 39.2549 206.483C39.1484 206.785 39.0198 207.056 38.8689 207.295C38.727 207.535 38.585 207.757 38.4431 207.961L35.8877 211.541C35.8522 211.585 35.8034 211.643 35.7413 211.714C35.6792 211.776 35.586 211.807 35.4618 211.807H34.7431ZM36.0341 207.508C36.4068 207.508 36.7484 207.428 37.0589 207.269C37.3783 207.1 37.6312 206.869 37.8175 206.577C38.0039 206.275 38.097 205.916 38.097 205.499C38.097 205.082 38.0039 204.727 37.8175 204.434C37.6312 204.132 37.3783 203.902 37.0589 203.742C36.7484 203.573 36.4068 203.489 36.0341 203.489C35.6703 203.489 35.3332 203.573 35.0226 203.742C34.7121 203.902 34.4636 204.132 34.2773 204.434C34.091 204.727 33.9978 205.082 33.9978 205.499C33.9978 205.916 34.091 206.275 34.2773 206.577C34.4636 206.869 34.7121 207.1 35.0226 207.269C35.3332 207.428 35.6703 207.508 36.0341 207.508ZM44.033 211.94C43.2965 211.94 42.671 211.794 42.1564 211.501C41.6418 211.199 41.2469 210.809 40.9719 210.33C40.7057 209.842 40.5726 209.314 40.5726 208.746C40.5726 208.48 40.6037 208.227 40.6658 207.987C40.7368 207.739 40.821 207.508 40.9186 207.295C41.0251 207.074 41.1272 206.878 41.2248 206.71C41.3312 206.532 41.4244 206.39 41.5042 206.284L44.033 202.757C44.0773 202.713 44.1306 202.659 44.1927 202.597C44.2548 202.526 44.348 202.491 44.4722 202.491H45.1776C45.2574 202.491 45.3195 202.522 45.3639 202.584C45.4171 202.637 45.4437 202.704 45.4437 202.784C45.4437 202.819 45.4393 202.855 45.4304 202.89C45.4216 202.917 45.4082 202.939 45.3905 202.957L43.4474 205.672C43.5538 205.636 43.6736 205.614 43.8067 205.605C43.9487 205.587 44.0818 205.578 44.206 205.578C44.694 205.587 45.1376 205.676 45.5369 205.845C45.9362 206.013 46.2778 206.244 46.5617 206.537C46.8545 206.821 47.0763 207.153 47.2272 207.535C47.378 207.908 47.4534 208.307 47.4534 208.733C47.4534 209.292 47.3203 209.815 47.0541 210.303C46.788 210.791 46.3976 211.186 45.8829 211.488C45.3772 211.789 44.7605 211.94 44.033 211.94ZM44.0197 210.809C44.3923 210.809 44.7384 210.729 45.0578 210.569C45.3772 210.41 45.6345 210.179 45.8297 209.877C46.0338 209.567 46.1358 209.19 46.1358 208.746C46.1358 208.294 46.0382 207.916 45.843 207.615C45.6478 207.304 45.3861 207.074 45.0578 206.923C44.7384 206.763 44.3923 206.683 44.0197 206.683C43.647 206.683 43.301 206.763 42.9816 206.923C42.6621 207.074 42.4004 207.304 42.1963 207.615C42.0011 207.916 41.9035 208.294 41.9035 208.746C41.9035 209.19 42.0011 209.567 42.1963 209.877C42.4004 210.179 42.6621 210.41 42.9816 210.569C43.301 210.729 43.647 210.809 44.0197 210.809Z"
                    fill="#797979"
                  />
                  <path
                    d="M317.654 211.807C317.565 211.807 317.489 211.781 317.427 211.727C317.374 211.665 317.347 211.59 317.347 211.501V211.075C317.347 210.995 317.37 210.907 317.414 210.809C317.458 210.711 317.547 210.605 317.68 210.49L320.502 207.668C320.954 207.286 321.309 206.963 321.566 206.696C321.833 206.421 322.019 206.16 322.125 205.911C322.241 205.663 322.298 205.405 322.298 205.139C322.298 204.634 322.156 204.234 321.873 203.941C321.589 203.64 321.167 203.489 320.608 203.489C320.253 203.489 319.947 203.564 319.69 203.715C319.433 203.857 319.228 204.057 319.078 204.314C318.936 204.563 318.842 204.846 318.798 205.166C318.78 205.281 318.732 205.361 318.652 205.405C318.581 205.45 318.514 205.472 318.452 205.472H317.76C317.68 205.472 317.614 205.45 317.56 205.405C317.507 205.352 317.481 205.29 317.481 205.219C317.489 204.882 317.56 204.545 317.693 204.208C317.827 203.862 318.022 203.551 318.279 203.276C318.545 203.001 318.869 202.779 319.251 202.611C319.641 202.433 320.094 202.344 320.608 202.344C321.309 202.344 321.881 202.473 322.325 202.73C322.778 202.979 323.11 203.311 323.323 203.729C323.536 204.146 323.643 204.602 323.643 205.099C323.643 205.49 323.576 205.849 323.443 206.177C323.31 206.497 323.115 206.812 322.857 207.122C322.609 207.424 322.298 207.735 321.926 208.054L319.37 210.649H323.523C323.62 210.649 323.696 210.676 323.749 210.729C323.811 210.782 323.842 210.858 323.842 210.955V211.501C323.842 211.59 323.811 211.665 323.749 211.727C323.696 211.781 323.62 211.807 323.523 211.807H317.654ZM328.726 211.94C328.113 211.94 327.594 211.847 327.168 211.661C326.751 211.466 326.41 211.199 326.144 210.862C325.877 210.525 325.682 210.139 325.558 209.704C325.443 209.27 325.376 208.808 325.358 208.32C325.349 208.081 325.341 207.828 325.332 207.562C325.332 207.295 325.332 207.029 325.332 206.763C325.341 206.488 325.349 206.226 325.358 205.978C325.367 205.49 325.434 205.028 325.558 204.594C325.691 204.15 325.886 203.764 326.144 203.436C326.41 203.099 326.756 202.837 327.182 202.65C327.608 202.455 328.122 202.358 328.726 202.358C329.338 202.358 329.852 202.455 330.269 202.65C330.695 202.837 331.041 203.099 331.308 203.436C331.574 203.764 331.769 204.15 331.893 204.594C332.026 205.028 332.097 205.49 332.106 205.978C332.115 206.226 332.119 206.488 332.119 206.763C332.128 207.029 332.128 207.295 332.119 207.562C332.119 207.828 332.115 208.081 332.106 208.32C332.097 208.808 332.026 209.27 331.893 209.704C331.769 210.139 331.574 210.525 331.308 210.862C331.05 211.199 330.709 211.466 330.283 211.661C329.866 211.847 329.347 211.94 328.726 211.94ZM328.726 210.809C329.418 210.809 329.928 210.583 330.256 210.13C330.593 209.669 330.766 209.043 330.775 208.254C330.793 207.996 330.802 207.748 330.802 207.508C330.802 207.26 330.802 207.016 330.802 206.776C330.802 206.528 330.793 206.284 330.775 206.044C330.766 205.272 330.593 204.656 330.256 204.194C329.928 203.724 329.418 203.489 328.726 203.489C328.042 203.489 327.532 203.724 327.195 204.194C326.867 204.656 326.694 205.272 326.676 206.044C326.676 206.284 326.672 206.528 326.663 206.776C326.663 207.016 326.663 207.26 326.663 207.508C326.672 207.748 326.676 207.996 326.676 208.254C326.694 209.043 326.871 209.669 327.208 210.13C327.545 210.583 328.051 210.809 328.726 210.809ZM333.913 211.807C333.824 211.807 333.749 211.781 333.687 211.727C333.634 211.665 333.607 211.59 333.607 211.501V211.075C333.607 210.995 333.629 210.907 333.673 210.809C333.718 210.711 333.807 210.605 333.94 210.49L336.761 207.668C337.214 207.286 337.569 206.963 337.826 206.696C338.092 206.421 338.278 206.16 338.385 205.911C338.5 205.663 338.558 205.405 338.558 205.139C338.558 204.634 338.416 204.234 338.132 203.941C337.848 203.64 337.427 203.489 336.868 203.489C336.513 203.489 336.207 203.564 335.949 203.715C335.692 203.857 335.488 204.057 335.337 204.314C335.195 204.563 335.102 204.846 335.058 205.166C335.04 205.281 334.991 205.361 334.911 205.405C334.84 205.45 334.774 205.472 334.712 205.472H334.019C333.94 205.472 333.873 205.45 333.82 205.405C333.767 205.352 333.74 205.29 333.74 205.219C333.749 204.882 333.82 204.545 333.953 204.208C334.086 203.862 334.281 203.551 334.539 203.276C334.805 203.001 335.129 202.779 335.51 202.611C335.9 202.433 336.353 202.344 336.868 202.344C337.569 202.344 338.141 202.473 338.584 202.73C339.037 202.979 339.37 203.311 339.583 203.729C339.796 204.146 339.902 204.602 339.902 205.099C339.902 205.49 339.836 205.849 339.702 206.177C339.569 206.497 339.374 206.812 339.117 207.122C338.868 207.424 338.558 207.735 338.185 208.054L335.63 210.649H339.782C339.88 210.649 339.955 210.676 340.009 210.729C340.071 210.782 340.102 210.858 340.102 210.955V211.501C340.102 211.59 340.071 211.665 340.009 211.727C339.955 211.781 339.88 211.807 339.782 211.807H333.913ZM344.732 211.94C344.173 211.94 343.685 211.874 343.268 211.741C342.851 211.599 342.501 211.412 342.217 211.182C341.933 210.951 341.715 210.698 341.565 210.423C341.423 210.148 341.347 209.864 341.338 209.571C341.338 209.491 341.365 209.429 341.418 209.385C341.471 209.341 341.538 209.318 341.618 209.318H342.27C342.35 209.318 342.421 209.336 342.483 209.372C342.545 209.407 342.598 209.482 342.643 209.598C342.731 209.917 342.882 210.166 343.095 210.343C343.317 210.521 343.57 210.645 343.854 210.716C344.147 210.778 344.439 210.809 344.732 210.809C345.344 210.809 345.841 210.663 346.223 210.37C346.604 210.077 346.795 209.647 346.795 209.079C346.795 208.502 346.622 208.089 346.276 207.841C345.93 207.593 345.451 207.468 344.839 207.468H343.574C343.486 207.468 343.41 207.442 343.348 207.389C343.295 207.335 343.268 207.26 343.268 207.162V206.803C343.268 206.723 343.281 206.657 343.308 206.603C343.344 206.541 343.379 206.488 343.415 206.444L345.997 203.622H342.07C341.982 203.622 341.906 203.595 341.844 203.542C341.791 203.489 341.764 203.414 341.764 203.316V202.81C341.764 202.713 341.791 202.637 341.844 202.584C341.906 202.522 341.982 202.491 342.07 202.491H347.381C347.478 202.491 347.554 202.522 347.607 202.584C347.669 202.637 347.7 202.713 347.7 202.81V203.289C347.7 203.351 347.682 203.409 347.647 203.462C347.62 203.507 347.589 203.551 347.554 203.595L344.985 206.457L345.198 206.47C345.784 206.488 346.294 206.594 346.729 206.79C347.172 206.985 347.514 207.278 347.753 207.668C348.002 208.05 348.126 208.533 348.126 209.119C348.126 209.713 347.98 210.223 347.687 210.649C347.394 211.066 346.99 211.386 346.476 211.607C345.961 211.829 345.38 211.94 344.732 211.94Z"
                    fill="#797979"
                  />
                </svg>
              </div>
            </div>
          </div>
        </section>
        <section className="container mx-auto mb-16 h-full px-8 xl:mb-24">
          <p
            className="font-semibold text-dark-2 md:w-3/4 lg:w-1/2 lg:text-xl xl:w-2/5"
            data-aos="fade-up"
          >
            {currentLanguage === "PT"
              ? "Desde 2018 a SENQUAL encontra-se acreditada pelo IPAC como "
              : "Since 2018, SENQUAL has been accredited by IPAC as "}
            <span className="uppercase text-green-1">
              {currentLanguage === "PT"
                ? "Laboratório de Ensaios"
                : "Testing Laboratory"}
            </span>{" "}
            {currentLanguage === "PT"
              ? "segundo a norma NP EN ISO IEC 17025:2018:"
              : "according to standard NP EN ISO IEC 17025:2018:"}
          </p>
        </section>
        <section className="container mx-auto mb-16 flex h-full flex-col gap-y-10 px-8 md:flex-row md:gap-x-8 xl:mb-32 xl:gap-x-24 2xl:gap-x-32">
          {acreditacoes}
        </section>
        <section className="bg-light-5 lg:hidden">
          <img
            src={process.env.PUBLIC_URL + "/img/sobre/sobre-4-mobile.jpg"}
            srcSet={process.env.PUBLIC_URL + "/img/sobre/sobre-4-mobile-2x.jpg"}
            alt="Sobre - Senqual"
            data-aos="fade-up"
          />
          <div className="container mx-auto px-8" data-aos="fade-up">
            <p className="ml-auto py-28 text-right text-xl font-medium text-green-1 underline md:w-3/4 lg:w-1/4">
              {currentLanguage === "PT"
                ? "Apoio aos clientes na execução de trabalho e ensaios em laboratório e in situ."
                : "Support for clients in the execution of work and laboratory and on-site tests."}
            </p>
          </div>
        </section>

        <section
          className="w-100 relative hidden bg-light-5 lg:flex"
          data-aos="fade-up"
        >
          <div
            className="mask-left animation-1 z-10 w-3/5 bg-green-4 drop-shadow-md"
            data-aos="fade-right"
            data-aos-delay="500"
          >
            <img
              src="img/sobre/sobre-4.jpg"
              srcSet="img/sobre/sobre-4-2x.jpg 2x"
              alt=""
              className="w-full"
            />
          </div>
          <div
            className="container absolute inset-x-0 inset-y-0 m-auto mx-auto flex items-center justify-end px-8"
            data-aos="fade-left"
            data-aos-delay="500"
          >
            <p className="w-2/5 text-right text-xl font-medium text-green-1 underline">
              {currentLanguage === "PT"
                ? "Apoio aos clientes na execução de trabalho e ensaios em laboratório e in situ."
                : "Support for clients in the execution of work and laboratory and on-site tests."}
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
